import { useState, useMemo, useReducer } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Badge,
  Card,
} from '@fluentui/react-components';
import {
  ChevronLeft24Regular,
  ChevronRight24Regular,
  ArrowDownload24Regular,
  Search24Regular,
  Filter24Regular,
} from '@fluentui/react-icons';
import dayjs from 'dayjs';
import { useReferrals, useReferralMetrics } from '@/api/hooks/useReferrals';
import { StatusBadge } from '@/components/referral/StatusBadge';
import { ReferralDetailDrawer } from '@/components/admin/ReferralDetailDrawer';
import { REFERRAL_STATUSES, ATA_LOCATIONS, STATUS_LABELS } from '@/config/services';
import type { ReferralStatus } from '@/config/services';
import type { ReferralDto } from '@/types/referral';

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  heading: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
    lineHeight: 1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  toolbar: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: `${tokens.borderRadiusLarge} ${tokens.borderRadiusLarge} 0 0`,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  searchInput: {
    minWidth: '200px',
    flexGrow: 1,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  serviceList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/admin/')({
  component: AdminDashboardPage,
});

// ─── Summary stats ────────────────────────────────────────────────────────────

interface StatCardProps {
  value: number | string;
  label: string;
  color?: string;
  fontSize?: string;
}

function StatCard({ value, label, color, fontSize }: StatCardProps) {
  const styles = useStyles();
  return (
    <Card>
      <div className={styles.statCard}>
        <Text className={styles.statValue} style={{ ...(color ? { color } : undefined), ...(fontSize ? { fontSize } : undefined) }} block>
          {value}
        </Text>
        <Text className={styles.statLabel} block>
          {label}
        </Text>
      </div>
    </Card>
  );
}

// ─── Admin table row ──────────────────────────────────────────────────────────

function AdminReferralRow({
  referral,
  onSelect,
}: {
  referral: ReferralDto;
  onSelect: (id: number) => void;
}) {
  const styles = useStyles();

  return (
    <TableRow className={styles.clickableRow} onClick={() => onSelect(referral.id)}>
      <TableCell>
        <TableCellLayout>{dayjs(referral.createdAt).format('MMM D, YYYY')}</TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          <div>
            <Text size={300} weight="semibold" block>
              {referral.clientContactName}
            </Text>
            {referral.businessName && (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {referral.businessName}
              </Text>
            )}
          </div>
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          <Text size={200}>{referral.referrerName}</Text>
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          <Text size={200}>{referral.referrerLocation}</Text>
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          <div className={styles.serviceList}>
            {referral.services.slice(0, 2).map((svc) => (
              <Badge key={svc} appearance="tint" color="informative" size="small">
                {svc}
              </Badge>
            ))}
            {referral.services.length > 2 && (
              <Badge appearance="tint" color="subtle" size="small">
                +{referral.services.length - 2}
              </Badge>
            )}
          </div>
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          <StatusBadge status={referral.status} />
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {referral.assignedToName || '—'}
          </Text>
        </TableCellLayout>
      </TableCell>
      <TableCell>
        <TableCellLayout>
          {referral.revenue != null ? (
            <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1 }}>
              ${referral.revenue.toLocaleString()}
            </Text>
          ) : (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              —
            </Text>
          )}
        </TableCellLayout>
      </TableCell>
    </TableRow>
  );
}

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportToCSV(referrals: ReferralDto[]) {
  const headers = [
    'ID',
    'Submitted',
    'Business Name',
    'Client Contact',
    'Client Email',
    'Client Phone',
    'Existing Client',
    'Referrer Name',
    'Referrer Email',
    'Referrer Location',
    'Services',
    'Status',
    'Revenue',
    'Notes',
  ];

  const escape = (val: string | null | undefined) => {
    if (val == null) return '';
    const s = String(val).replace(/"/g, '""');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
  };

  const rows = referrals.map((r) => [
    r.id,
    dayjs(r.createdAt).format('YYYY-MM-DD'),
    escape(r.businessName),
    escape(r.clientContactName),
    escape(r.clientEmail),
    escape(r.clientPhone),
    r.isExistingClient ? 'Yes' : 'No',
    escape(r.referrerName),
    escape(r.referrerEmail),
    escape(r.referrerLocation),
    escape(r.services.join('; ')),
    r.status,
    r.revenue ?? '',
    escape(r.statusComments),
  ]);

  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ata-referrals-${dayjs().format('YYYY-MM-DD')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Filter state ────────────────────────────────────────────────────────────

interface FilterState {
  page: number;
  search: string;
  statusFilter: ReferralStatus | '';
  locationFilter: string;
  fromDate: string;
  toDate: string;
}

type FilterAction =
  | { type: 'setPage'; page: number }
  | { type: 'setFilter'; patch: Partial<Omit<FilterState, 'page'>> };

const initialFilterState: FilterState = {
  page: 1,
  search: '',
  statusFilter: '',
  locationFilter: '',
  fromDate: '',
  toDate: '',
};

function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'setPage':
      return { ...state, page: action.page };
    case 'setFilter':
      return { ...state, ...action.patch, page: 1 };
  }
}

// ─── Main page component ──────────────────────────────────────────────────────

function AdminDashboardPage() {
  const styles = useStyles();
  const [filters, dispatch] = useReducer(filterReducer, initialFilterState);
  const [selectedReferralId, setSelectedReferralId] = useState<number | null>(null);
  const PAGE_SIZE = 20;

  const { page, search, statusFilter, locationFilter, fromDate, toDate } = filters;

  const { data, isLoading, isError, error } = useReferrals({
    page,
    pageSize: PAGE_SIZE,
    status: statusFilter,
    location: locationFilter,
    search,
    fromDate,
    toDate,
  });

  const referrals = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const selectedReferral = useMemo(
    () => referrals.find((r) => r.id === selectedReferralId) ?? null,
    [referrals, selectedReferralId],
  );

  const { data: metrics } = useReferralMetrics();

  const handleExport = () => {
    exportToCSV(referrals);
  };

  return (
    <div className={styles.page}>
      {/* Heading */}
      <div>
        <Text className={styles.heading} block>
          Admin Dashboard
        </Text>
        <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
          View and manage all cross-sell referrals across ATA
        </Text>
      </div>

      {/* Summary cards */}
      <div className={styles.summaryGrid}>
        <StatCard value={metrics?.totalReferrals ?? 0} label="Total Referrals" />
        <StatCard
          value={metrics?.submittedThisMonth ?? 0}
          label="This Month"
        />
        <StatCard
          value={(metrics?.qualifiedCount ?? 0) + (metrics?.wonCount ?? 0)}
          label="Qualified / Won"
          color={tokens.colorPaletteGreenForeground1}
        />
        <StatCard value={metrics?.wonCount ?? 0} label="Won" color={tokens.colorBrandForeground1} />
        <StatCard
          value={`$${(metrics?.totalRevenue ?? 0).toLocaleString()}`}
          label="Total Revenue"
          color={tokens.colorPaletteGreenForeground1}
          fontSize={tokens.fontSizeBase600}
        />
        <StatCard
          value={`$${(metrics?.totalCommissions ?? 0).toLocaleString()}`}
          label="Total Commissions"
          color={tokens.colorPaletteGreenForeground1}
          fontSize={tokens.fontSizeBase600}
        />
        <StatCard
          value={`$${(metrics?.pendingCommissions ?? 0).toLocaleString()}`}
          label="Pending Payouts"
          color={tokens.colorPaletteDarkOrangeForeground1}
          fontSize={tokens.fontSizeBase600}
        />
      </div>

      {/* Error state */}
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {error instanceof Error ? error.message : 'Failed to load referrals.'}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Table card with toolbar */}
      <div className={styles.card}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <Input
            className={styles.searchInput}
            placeholder="Search by name, email, business..."
            value={search}
            onChange={(_e, d) => dispatch({ type: 'setFilter', patch: { search: d.value } })}
            contentBefore={<Search24Regular />}
          />

          <Dropdown
            placeholder="Status"
            value={statusFilter ? STATUS_LABELS[statusFilter] : ''}
            selectedOptions={statusFilter ? [statusFilter] : []}
            onOptionSelect={(_e, d) =>
              dispatch({ type: 'setFilter', patch: { statusFilter: (d.optionValue as ReferralStatus | '') ?? '' } })
            }
            style={{ minWidth: '140px' }}
          >
            <Option value="" text="All Statuses">
              All Statuses
            </Option>
            {REFERRAL_STATUSES.map((s) => (
              <Option key={s} value={s}>
                {STATUS_LABELS[s]}
              </Option>
            ))}
          </Dropdown>

          <Dropdown
            placeholder="Location"
            value={locationFilter}
            selectedOptions={locationFilter ? [locationFilter] : []}
            onOptionSelect={(_e, d) =>
              dispatch({ type: 'setFilter', patch: { locationFilter: d.optionValue ?? '' } })
            }
            style={{ minWidth: '140px' }}
          >
            <Option value="" text="All Locations">
              All Locations
            </Option>
            {ATA_LOCATIONS.map((loc) => (
              <Option key={loc} value={loc}>
                {loc}
              </Option>
            ))}
          </Dropdown>

          <Input
            type="date"
            value={fromDate}
            onChange={(_e, d) => dispatch({ type: 'setFilter', patch: { fromDate: d.value } })}
            contentBefore={<Filter24Regular style={{ fontSize: '16px' }} />}
            placeholder="From date"
            title="From date"
            aria-label="Filter from date"
            style={{ minWidth: '140px' }}
          />

          <Input
            type="date"
            value={toDate}
            onChange={(_e, d) => dispatch({ type: 'setFilter', patch: { toDate: d.value } })}
            placeholder="To date"
            title="To date"
            aria-label="Filter to date"
            style={{ minWidth: '140px' }}
          />

          <Button
            appearance="secondary"
            icon={<ArrowDownload24Regular />}
            onClick={handleExport}
            disabled={referrals.length === 0}
          >
            Export CSV
          </Button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
            <Spinner label="Loading referrals..." />
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Client</TableHeaderCell>
                  <TableHeaderCell>Referrer</TableHeaderCell>
                  <TableHeaderCell>Location</TableHeaderCell>
                  <TableHeaderCell>Services</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Assigned To</TableHeaderCell>
                  <TableHeaderCell>Revenue</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <div className={styles.emptyState}>
                        <Text>No referrals found matching your filters.</Text>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  referrals.map((r) => (
                    <AdminReferralRow
                      key={r.id}
                      referral={r}
                      onSelect={setSelectedReferralId}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        <div className={styles.pagination}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
            {totalCount > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} referrals`
              : 'No results'}
          </Text>
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' }}>
            <Button
              appearance="subtle"
              icon={<ChevronLeft24Regular />}
              disabled={page <= 1}
              onClick={() => dispatch({ type: 'setPage', page: Math.max(1, page - 1) })}
              aria-label="Previous page"
            />
            <Text size={200}>
              Page {page} of {totalPages}
            </Text>
            <Button
              appearance="subtle"
              icon={<ChevronRight24Regular />}
              disabled={page >= totalPages}
              onClick={() => dispatch({ type: 'setPage', page: Math.min(totalPages, page + 1) })}
              aria-label="Next page"
            />
          </div>
        </div>
      </div>

      {/* Detail Drawer */}
      <ReferralDetailDrawer
        referral={selectedReferral}
        open={selectedReferralId !== null}
        onClose={() => setSelectedReferralId(null)}
      />
    </div>
  );
}
